import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Link, Typography } from "@mui/material";

import { useFormattedTimestamp } from "../../../../../hooks/useFormattedTimestamp";

import { useUserDataFormatter } from "../../../../User/hooks/useUserDataFormatter";

import { GoServiceNotification, Notification, isGoService } from "../../../types/notification";
import { getNotificationRoute } from "../../../utils/getNotificationRoute";
import GoanlgoNotificationAvatar from "../../../assets/images/goalngo_notification_avatar.png";

const NotificationItem = ({
  notification,
  lastSeenTime,
  onClose,
}: {
  notification: Notification;
  lastSeenTime?: number;
  onClose: () => void;
}) => {
  const { t } = useTranslation("layout", { keyPrefix: "notifications" });
  const { t: tBody } = useTranslation("layout", { keyPrefix: "notifications.notificationsBody" });
  const navigate = useNavigate();

  const { createdAt, additionalProperties, count } = notification;
  const { user } = additionalProperties;
  const { fullName, firstTwoChr, media, profileLink } = useUserDataFormatter(user);

  const formattedTime = useFormattedTimestamp(createdAt);
  const IS_UNREAD = lastSeenTime ? createdAt > lastSeenTime : true;

  const handleUserProfileNavigation = () => {
    if (user) navigate(profileLink);
  };

  const route = getNotificationRoute(notification);
  const handleNotificationNavigation = () => {
    if (route) {
      navigate(route);
      onClose();
    }
  };
  const isLink = Boolean(route);

  return (
    <Box
      sx={styles.container(isLink)}
      component={isLink ? Link : "span"}
      onClick={isLink ? handleNotificationNavigation : undefined}
    >
      <Avatar onClick={handleUserProfileNavigation} src={user ? media : GoanlgoNotificationAvatar} sx={styles.avatar}>
        {firstTwoChr}
      </Avatar>
      <Box sx={styles.contentContainer}>
        {IS_UNREAD && <Box sx={styles.unreadIndicator} />}
        <Box sx={styles.header}>
          {user ? (
            <Typography sx={styles.headingText}>
              <Link
                onClick={handleUserProfileNavigation}
                title={fullName}
                sx={{
                  textDecoration: "none",
                  cursor: "pointer",
                  color: "inherit",
                }}
              >
                {fullName}
              </Link>
              {t("andOther", { count: count })}
            </Typography>
          ) : (
            <Typography sx={styles.headingText}>{t("goalngo")}</Typography>
          )}
          <Typography sx={styles.timeAgo}>{formattedTime}</Typography>
        </Box>
        {isGoService(notification) ||
        // @ts-expect-error - notification is a GoServiceNotification that I did not add to the types
        (notification.title && notification.message) ? (
          <>
            <Typography
              sx={styles.notificationTitle}
              title={(notification as GoServiceNotification).title}
              onClick={handleNotificationNavigation}
            >
              {(notification as GoServiceNotification).title}
            </Typography>
            <Typography sx={styles.notificationDescription}>
              {(notification as GoServiceNotification).message}
            </Typography>
          </>
        ) : (
          <Typography sx={styles.notificationDescription}>{tBody(notification.type, { defaultValue: "" })}</Typography>
        )}
      </Box>
    </Box>
  );
};

export default NotificationItem;

const styles = {
  container: (isLink: boolean) => ({
    width: "100%",
    display: "flex",
    columnGap: "12px",
    pr: "8px",
    pl: "24px",
    py: "12px",
    cursor: isLink ? "pointer" : "default",
    textDecoration: "none",
    "&:hover": {
      background: isLink ? "var(--Dark---50, #F5F5F5)" : "transparent",
    },
  }),
  avatar: {
    width: "48px",
    height: "48px",
    cursor: "pointer",
  },
  contentContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    position: "relative",
  },
  unreadIndicator: {
    position: "absolute",
    top: "0",
    right: "0",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    background: "#12B76A",
  },
  header: {
    display: "flex",
    width: "fit-content",
    columnGap: "8px",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },

  headingText: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
    cursor: "pointer",
    lineClamp: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 1,
    WebkitBoxOrient: "vertical",
  },
  timeAgo: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "18px",
    textWrap: "nowrap",
  },
  notificationTitle: {
    textDecoration: "none",
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
    lineClamp: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 1,
    WebkitBoxOrient: "vertical",
  },
  notificationDescription: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "18px",
  },
};
